/**
 * Utility functions for price calculations
 */

/**
 * Calculate discount percentage between regular price and sale price
 * @param {number|string} regularPrice - The regular price of the item
 * @param {number|string} salePrice - The sale price of the item
 * @returns {number|null} - The discount percentage (0-100) or null if no discount
 */
export const calculateDiscountPercentage = (regularPrice, salePrice) => {
  // Convert to numbers if they are strings
  const regular = typeof regularPrice === 'string' ? parseFloat(regularPrice) : regularPrice;
  const sale = typeof salePrice === 'string' ? parseFloat(salePrice) : salePrice;

  // Check if both prices are valid numbers and sale price is less than regular price
  if (
    !regular || 
    !sale || 
    isNaN(regular) || 
    isNaN(sale) || 
    sale >= regular ||
    regular <= 0 ||
    sale <= 0
  ) {
    return null;
  }

  // Calculate discount percentage and round to nearest integer
  return Math.round(((regular - sale) / regular) * 100);
};

/**
 * Check if a product has a discount
 * @param {number|string} regularPrice - The regular price of the item
 * @param {number|string} salePrice - The sale price of the item
 * @returns {boolean} - True if the product has a discount
 */
export const hasDiscount = (regularPrice, salePrice) => {
  const discountPercentage = calculateDiscountPercentage(regularPrice, salePrice);
  return discountPercentage !== null && discountPercentage > 0;
};

/**
 * Format discount percentage for display
 * @param {number|string} regularPrice - The regular price of the item
 * @param {number|string} salePrice - The sale price of the item
 * @param {boolean} withMinus - Whether to include minus sign (default: true)
 * @returns {string|null} - Formatted discount string (e.g., "-25%") or null
 */
export const formatDiscountPercentage = (regularPrice, salePrice, withMinus = true) => {
  const percentage = calculateDiscountPercentage(regularPrice, salePrice);
  
  if (percentage === null) {
    return null;
  }
  
  return `${withMinus ? '-' : ''}${percentage}%`;
};
